<template>
  <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
    <router-link
      :to="{ name: 'Absences', query: { month: date.format('YYYY-MM') } }"
      class="btn btn-sm btn-outline-secondary"
      >&larr; Delo</router-link
    >
    <h1 class="h3 mb-0 me-auto">
      <template v-if="day">{{ day.worker.lastName }} {{ day.worker.firstName }}, </template
      >{{ date.format("dd D.M.YYYY") }}
    </h1>
    <div class="btn-group btn-group-sm">
      <router-link :to="dayRoute(-1)" class="btn btn-outline-secondary" title="Prejšnji dan">&lsaquo;</router-link>
      <router-link :to="dayRoute(1)" class="btn btn-outline-secondary" title="Naslednji dan">&rsaquo;</router-link>
    </div>
  </div>

  <div class="card">
    <div class="card-body small">
      <div v-if="day" class="row g-3">
        <div class="col-lg-4">
          <h6>Vpisane poti</h6>
          <ul v-if="day.transports.length" class="list-unstyled mb-0">
            <li v-for="(t, i) in day.transports" :key="t.id" class="mb-2">
              <div>
                <span class="fw-semibold">{{ time(t.startDateTime) }} – {{ time(t.endDateTime) }}</span>
                <span class="text-muted"> ({{ duration(t) }})</span>
                <span v-if="!t.isPaid" class="badge bg-secondary ms-1" title="Prihod/odhod iz službe, ni plačano"
                  >Na/iz</span
                >
                <span v-if="t.isPassenger" class="badge bg-info-subtle text-info-emphasis ms-1">Sovoznik</span>
              </div>
              <div>
                <router-link v-if="orderRoute(t)" :to="orderRoute(t)">{{ orderText(t) }}</router-link>
                <span v-if="t.facility"> {{ t.facility }}</span>
              </div>
              <div class="text-muted">
                {{
                  [
                    t.relation,
                    t.personalKilometers > 0 ? `${+t.personalKilometers} km` : "",
                    vehicle(day.transports, i),
                    t.comments,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                }}
              </div>
            </li>
          </ul>
          <p v-else class="text-muted mb-0">Ni vpisanih poti.</p>
        </div>

        <div class="col-lg-4">
          <h6
            title="Odhodi vozila (sledilnik) proti cilju, ki ga je delavec izbral (Kam greš?), in čas vožnje po Googlu ob odhodu; brez izbire do prve odobrene lokacije, brez prometa"
          >
            Odhodi vozila in idealni čas poti
          </h6>
          <ul v-if="day.trips.length" class="list-unstyled mb-0">
            <li v-for="(t, i) in day.trips" :key="t.id" class="mb-2">
              <div>
                <span class="fw-semibold">{{ time(t.departureTime) }}</span>
                {{ place(t.originKind, t.originName) }} →
                <template v-if="t.coworkerName">👥 {{ t.coworkerName }} → </template>
                {{ place(t.targetKind, t.targetName) }}
                <span
                  v-if="!t.isDeclared"
                  class="badge bg-warning-subtle text-warning-emphasis ms-1"
                  title="Delavec ni izbral cilja"
                  >Ni izbral</span
                >
                <router-link
                  v-if="t.targetServiceOrderId"
                  :to="{ name: 'ServiceOrder', params: { workOrderId: t.targetServiceOrderId } }"
                  >{{ t.targetServiceOrderNumber || "SDN" }}</router-link
                >
              </div>
              <div class="text-muted">
                {{ [ideal(t), km(t.directMeters), vehicle(day.trips, i)].filter(Boolean).join(" · ") }}
              </div>
              <div v-if="t.error" class="text-danger">{{ t.error }}</div>
            </li>
          </ul>
          <p v-else class="text-muted mb-0">Ni odhodov.</p>
        </div>

        <div class="col-lg-4">
          <h6>Plan</h6>
          <ol v-if="day.waypoints.length" class="ps-3 mb-0">
            <li v-for="wp in day.waypoints" :key="wp.id" class="mb-2">
              <span class="fw-semibold">{{ wp.time?.slice(0, 5) }}</span>
              <router-link
                v-if="wp.serviceOrderID !== $EMPTY_GUID"
                :to="{ name: 'ServiceOrder', params: { workOrderId: wp.serviceOrderID } }"
                class="ms-1"
                >{{ wp.serviceOrderNumber }}</router-link
              >
              {{ wp.name }}
              <span v-if="wp.serviceOrderIsCompleted" class="badge bg-success-subtle text-success-emphasis ms-1"
                >Zaključen</span
              >
              <div v-if="wp.serviceOrderSubject" class="text-muted">{{ wp.serviceOrderSubject }}</div>
            </li>
          </ol>
          <p v-else class="text-muted mb-0">Ni plana.</p>
        </div>
      </div>
    </div>
  </div>

  <AbsenceDayMap :day="day" :date="date" />
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue"
import { useRoute } from "vue-router"
import dayjs from "dayjs"
import { useAbsences } from "../composition-api/useAbsences"
import { useEnums } from "../composition-api/useEnums"
import AbsenceDayMap from "../components/AbsenceDayMap.vue"

defineOptions({ name: "AbsenceDay" })

const route = useRoute()
const workerId = computed(() => route.params.workerId)
const date = computed(() => dayjs(route.params.date))
const dayRoute = (offset) => ({
  name: "AbsenceDay",
  params: { workerId: workerId.value, date: date.value.add(offset, "day").format("YYYY-MM-DD") },
})

const { loadAbsenceDayAsync } = useAbsences()
const { internalOrderTypes, loadEnumsAsync } = useEnums()

const day = ref(null) // { worker, transports, trips, waypoints, trackers }

const time = (value) => (value ? dayjs(value).format("HH:mm") : "…")
const PLACES = {
  Home: "Doma",
  Warehouse: "Skladišče",
  Facility: "Objekt",
  Other: "Drugo",
  Shopping: "Nabava",
  Private: "Privat",
  Unknown: "Na poti?",
}
const place = (kind, name) => (kind === "Home" ? PLACES.Home : name || PLACES[kind] || kind)
const ideal = (t) =>
  t.directSeconds != null
    ? `idealno ${Math.round(t.directSeconds / 60)} min${t.isTrafficAware ? "" : " (brez prometa)"}`
    : ""
const km = (meters) => (meters != null ? `${Math.round(meters / 1000)} km` : "")
const duration = (t) => (t.endDateTime ? `${dayjs(t.endDateTime).diff(t.startDateTime, "minute")} min` : "ni končano")

// the car is switched during the day very rarely: show it only when the day has more than one,
// on the first entry and wherever it changes
const multipleVehicles = computed(
  () => new Set([...day.value.transports, ...day.value.trips].map((t) => t.vehicle).filter(Boolean)).size > 1,
)
const vehicle = (list, i) =>
  multipleVehicles.value && (i === 0 || list[i].vehicle !== list[i - 1].vehicle) ? list[i].vehicle : ""

const orderRoute = (t) => {
  if (t.serviceOrderId) return { name: "ServiceOrder", params: { workOrderId: t.serviceOrderId } }
  if (t.productionOrderId) return { name: "ProductionOrder", params: { workOrderId: t.productionOrderId } }
  if (t.internalOrderId) return { name: "InternalOrder", params: { workOrderId: t.internalOrderId } }
  return null
}
const orderText = (t) =>
  t.internalOrderId ? internalOrderTypes.value.get(t.internalType) || t.orderNumber : t.orderNumber

const loadAsync = async () => {
  const key = route.fullPath
  day.value = null
  const raw = await loadAbsenceDayAsync(workerId.value, date.value)
  if (raw) {
    // earliest first in every column (and in the map's numbering)
    const byTime = (value) => (a, b) => (value(a) ?? "").localeCompare(value(b) ?? "")
    raw.transports.sort(byTime((t) => dayjs(t.startDateTime).toISOString()))
    raw.trips.sort(byTime((t) => t.departureTime))
    raw.waypoints.sort((a, b) => byTime((wp) => wp.time)(a, b) || a.sequence - b.sequence)
  }
  // another day was opened meanwhile
  if (key === route.fullPath) day.value = raw
}

watch(() => [workerId.value, route.params.date], loadAsync)
onMounted(async () => {
  await Promise.all([loadAsync(), loadEnumsAsync()])
})
</script>
